import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';

interface Machine {
  id: number;
  number: string;
  isActive: boolean;
}

interface Chip {
  id: number;
  name: string;
  machineId: number;
  status: 'active' | 'inactive' | 'maintenance';
  lastSeen: string | null;
  isActive: boolean;
  chipNumber?: string | null;
  machine: Machine;
}

interface AddChipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChipAdded: () => void;
  editingChip?: Chip | null;
}

const AddChipModal: React.FC<AddChipModalProps> = ({ isOpen, onClose, onChipAdded, editingChip }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [formData, setFormData] = useState({
    name: '',
    machineId: '',
    status: 'active',
    chipNumber: ''
  });
  const [bulkFormData, setBulkFormData] = useState({
    from: '',
    to: '',
    prefix: 'CHIP',
    machineId: '',
    status: 'active',
    chipNumberPrefix: ''
  });
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchMachines();
      // Populate form if editing
      if (editingChip) {
        setFormData({
          name: editingChip.name,
          machineId: editingChip.machineId.toString(),
          status: editingChip.status,
          chipNumber: editingChip.chipNumber || ''
        });
      } else {
        // Reset form for new chip
        setFormData({
          name: '',
          machineId: '',
          status: 'active',
          chipNumber: ''
        });
      }
    }
  }, [isOpen, editingChip]);

  const fetchMachines = async () => {
    try {
      const response = await apiService.get('/machines?active=true');
      setMachines(response.machines || []);
    } catch (error) {
      console.error('Error fetching machines:', error);
      setError('Failed to fetch machines');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'bulk') {
        // Bulk create
        const bulkData = {
          from: parseInt(bulkFormData.from),
          to: parseInt(bulkFormData.to),
          prefix: bulkFormData.prefix,
          machineId: bulkFormData.machineId ? parseInt(bulkFormData.machineId) : null,
          status: bulkFormData.status,
          chipNumberPrefix: bulkFormData.chipNumberPrefix || null
        };
        await apiService.post('/chips/bulk', bulkData);
      } else {
        // Single create or update
        const chipData = {
          name: formData.name.trim(),
          machineId: parseInt(formData.machineId),
          status: formData.status,
          chipNumber: formData.chipNumber.trim() || null
        };

        if (editingChip) {
          // Update existing chip
          await apiService.put(`/chips/${editingChip.id}`, chipData);
        } else {
          // Create new chip
          await apiService.post('/chips', chipData);
        }
      }
      
      // Reset forms
      setFormData({
        name: '',
        machineId: '',
        status: 'active',
        chipNumber: ''
      });
      setBulkFormData({
        from: '',
        to: '',
        prefix: 'CHIP',
        machineId: '',
        status: 'active',
        chipNumberPrefix: ''
      });
      
      onChipAdded();
      onClose();
    } catch (error: any) {
      console.error('Error saving chip:', error);
      setError(error.response?.data?.message || `Failed to ${editingChip ? 'update' : 'create'} chip`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBulkFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClose = () => {
    setFormData({
      name: '',
      machineId: '',
      status: 'active',
      chipNumber: ''
    });
    setBulkFormData({
      from: '',
      to: '',
      prefix: 'CHIP',
      machineId: '',
      status: 'active',
      chipNumberPrefix: ''
    });
    setMode('single');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  // Don't show bulk mode when editing
  const showBulkMode = !editingChip;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingChip ? 'Edit Chip' : 'Add New Chip'}
          </h2>
        </div>
        
        {showBulkMode && (
          <div className="px-6 pt-4 border-b border-gray-200">
            <div className="flex space-x-1">
              <button
                type="button"
                onClick={() => setMode('single')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                  mode === 'single'
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Single
              </button>
              <button
                type="button"
                onClick={() => setMode('bulk')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                  mode === 'bulk'
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Bulk
              </button>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {mode === 'bulk' ? (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="prefix" className="block text-sm font-medium text-gray-700 mb-2">
                    Name Prefix
                  </label>
                  <input
                    type="text"
                    id="prefix"
                    name="prefix"
                    value={bulkFormData.prefix}
                    onChange={handleBulkInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="CHIP"
                  />
                </div>
                <div>
                  <label htmlFor="chipNumberPrefix" className="block text-sm font-medium text-gray-700 mb-2">
                    Chip Number Prefix (Optional)
                  </label>
                  <input
                    type="text"
                    id="chipNumberPrefix"
                    name="chipNumberPrefix"
                    value={bulkFormData.chipNumberPrefix}
                    onChange={handleBulkInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="CN"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="from" className="block text-sm font-medium text-gray-700 mb-2">
                    From *
                  </label>
                  <input
                    type="number"
                    id="from"
                    name="from"
                    value={bulkFormData.from}
                    onChange={handleBulkInputChange}
                    required
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label htmlFor="to" className="block text-sm font-medium text-gray-700 mb-2">
                    To *
                  </label>
                  <input
                    type="number"
                    id="to"
                    name="to"
                    value={bulkFormData.to}
                    onChange={handleBulkInputChange}
                    required
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="100"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="bulkMachineId" className="block text-sm font-medium text-gray-700 mb-2">
                  Machine (Optional)
                </label>
                <select
                  id="bulkMachineId"
                  name="machineId"
                  value={bulkFormData.machineId}
                  onChange={handleBulkInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No machine assigned</option>
                  {machines.map((machine) => (
                    <option key={machine.id} value={machine.id}>
                      Machine #{machine.number}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="bulkStatus" className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  id="bulkStatus"
                  name="status"
                  value={bulkFormData.status}
                  onChange={handleBulkInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Chip Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter chip name"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="chipNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Chip Number (Optional)
                </label>
                <input
                  type="text"
                  id="chipNumber"
                  name="chipNumber"
                  value={formData.chipNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter chip number"
                />
                <p className="mt-1 text-sm text-gray-500">Used for lookup with tenant ID when storing logs</p>
              </div>

              <div className="mb-4">
                <label htmlFor="machineId" className="block text-sm font-medium text-gray-700 mb-2">
                  Machine *
                </label>
                <select
                  id="machineId"
                  name="machineId"
                  value={formData.machineId}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a machine</option>
                  {machines.map((machine) => (
                    <option key={machine.id} value={machine.id}>
                      Machine #{machine.number}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            </>
          )}


          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading 
                ? 'Saving...' 
                : editingChip 
                  ? 'Update Chip' 
                  : mode === 'bulk' 
                    ? 'Create Chips' 
                    : 'Save Chip'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddChipModal;
